import React, { useEffect, useState } from 'react';
import { analyticsApi, adminApi } from '../../api/client';
import Spinner from '../../components/common/Spinner';
import StatCard from '../../components/common/StatCard';
import { FaChartBar, FaGraduationCap, FaCalendarCheck, FaTrophy } from 'react-icons/fa';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, LineChart, Line, Legend,
} from 'recharts';

const gradeColor = (v) => v >= 75 ? '#10b981' : v >= 50 ? '#f59e0b' : '#ef4444';
const gradeSymbol = (v) => v >= 80 ? 'A' : v >= 70 ? 'B' : v >= 60 ? 'C' : v >= 50 ? 'D' : v >= 40 ? 'E' : 'F';

export default function Analytics() {
  const [data,    setData]    = useState(null);
  const [terms,   setTerms]   = useState([]);
  const [termId,  setTermId]  = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { adminApi.getTerms().then(r => setTerms(r.data.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    analyticsApi.getSchool(termId || 0)
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [termId]);

  const radarData = data?.by_subject?.slice(0, 8).map(s => ({
    subject: s.subject_name.length > 12 ? s.subject_name.slice(0,12)+'…' : s.subject_name,
    avg: parseFloat(s.avg_pct) || 0,
  }));

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div><h2>School Analytics</h2><p>Performance overview across all grades and subjects</p></div>
        <select className="sms-input" style={{ width:200 }} value={termId} onChange={e => setTermId(e.target.value)}>
          <option value="">All Terms</option>
          {terms.map(t => <option key={t.term_id} value={t.term_id}>{t.name} {t.year}</option>)}
        </select>
      </div>

      {loading ? <Spinner /> : !data ? (
        <div className="sms-card" style={{ textAlign:'center', color:'#9ca3af', padding:'60px 0' }}>No data yet — add assessments and marks first</div>
      ) : (
        <>
          {/* Summary stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:16, marginBottom:28 }}>
            <StatCard label="Avg Attendance" value={`${data.attendance?.pct ?? 0}%`} icon={FaCalendarCheck}
              color={data.attendance?.pct >= 75 ? '#10b981' : '#ef4444'} sub={`${data.attendance?.present ?? 0} / ${data.attendance?.total ?? 0} days`} />
            <StatCard label="Grades Tracked"  value={data.by_grade?.length ?? 0}     icon={FaGraduationCap} color="#0d6efd" />
            <StatCard label="Subjects"         value={data.by_subject?.length ?? 0}   icon={FaChartBar}      color="#8b5cf6" />
            <StatCard label="Top Learners"     value={data.top_learners?.length ?? 0} icon={FaTrophy}        color="#f59e0b" />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, marginBottom:24 }}>
            {/* Performance per grade */}
            <div className="sms-card">
              <h5 style={{ fontWeight:700, marginBottom:16 }}>Average per Grade</h5>
              {data.by_grade?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.by_grade} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="grade_number" tickFormatter={v=>`Gr ${v}`} tick={{fontSize:11}} />
                    <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:11}} />
                    <Tooltip formatter={v=>[`${v}%`,'Average']} />
                    <Bar dataKey="avg_pct" name="Average %" radius={[6,6,0,0]}
                      fill="#0d6efd"
                      label={{ position:'top', fontSize:10, formatter: v => v ? `${v}%` : '' }} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p style={{color:'#9ca3af',textAlign:'center',paddingTop:40}}>No grade data</p>}
            </div>

            {/* Radar per subject */}
            <div className="sms-card">
              <h5 style={{ fontWeight:700, marginBottom:16 }}>Subject Performance Radar</h5>
              {radarData?.length > 2 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" tick={{fontSize:10}} />
                    <Radar dataKey="avg" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.25} name="Average %" />
                    <Tooltip formatter={v=>[`${v}%`,'Avg']} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : <p style={{color:'#9ca3af',textAlign:'center',paddingTop:40}}>Need at least 3 subjects with data</p>}
            </div>
          </div>

          {/* Attendance trend */}
          {data.attendance_trend?.length > 1 && (
            <div className="sms-card" style={{ marginBottom:24 }}>
              <h5 style={{ fontWeight:700, marginBottom:16 }}>Attendance Trend (Last 6 Months)</h5>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data.attendance_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="month" tick={{fontSize:11}} />
                  <YAxis tick={{fontSize:11}} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="present" stroke="#10b981" strokeWidth={2} dot={false} name="Present" />
                  <Line type="monotone" dataKey="absent"  stroke="#ef4444" strokeWidth={2} dot={false} name="Absent" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Subject table */}
          <div className="sms-card" style={{ marginBottom:24, padding:0, overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', fontWeight:700, borderBottom:'1px solid #e5e7eb' }}>Subject Breakdown</div>
            <table className="sms-table">
              <thead><tr><th>Subject</th><th>Learners</th><th>Average</th><th>Symbol</th><th>Passing</th><th>At Risk</th></tr></thead>
              <tbody>
                {data.by_subject?.map(s => {
                  const avg = parseFloat(s.avg_pct);
                  const col = gradeColor(avg);
                  return (
                    <tr key={s.subject_id}>
                      <td style={{fontWeight:600}}>{s.subject_name}</td>
                      <td>{s.learner_count}</td>
                      <td><span style={{fontWeight:700,color:col}}>{avg}%</span></td>
                      <td><span style={{fontWeight:800,fontSize:'1.05rem',color:col}}>{gradeSymbol(avg)}</span></td>
                      <td style={{color:'#10b981',fontWeight:600}}>{s.passing}</td>
                      <td style={{color:'#ef4444',fontWeight:600}}>{s.failing}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Top learners */}
          {data.top_learners?.length > 0 && (
            <div className="sms-card" style={{ padding:0, overflow:'hidden' }}>
              <div style={{ padding:'16px 20px', fontWeight:700, borderBottom:'1px solid #e5e7eb', display:'flex', alignItems:'center', gap:8 }}>
                <FaTrophy color="#f59e0b" /> Top 10 Learners — School Wide
              </div>
              <table className="sms-table">
                <thead><tr><th>#</th><th>Learner</th><th>Grade</th><th>Average</th><th>Symbol</th></tr></thead>
                <tbody>
                  {data.top_learners.map((l, i) => {
                    const avg = parseFloat(l.avg_pct);
                    const col = gradeColor(avg);
                    return (
                      <tr key={l.user_id}>
                        <td style={{ fontWeight:800, color: i===0?'#f59e0b':i===1?'#94a3b8':i===2?'#cd7c2f':'#6b7280', fontSize:'1.1rem' }}>
                          {i===0?'🥇':i===1?'🥈':i===2?'🥉':`#${i+1}`}
                        </td>
                        <td style={{fontWeight:600}}>{l.name}</td>
                        <td>Grade {l.grade_number}</td>
                        <td><span style={{fontWeight:700,color:col}}>{avg}%</span></td>
                        <td><span style={{fontWeight:800,color:col}}>{gradeSymbol(avg)}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
